import InlineNotification from '@components/shared/InlineNotification'
import store from 'stores/store'
import { walletBalanceForToken } from 'utils/tokens'
import { floorToDecimal, formatCurrencyValue } from 'utils/numbers'
import { useCallback, useEffect, useMemo, useState } from 'react'
import SearchModal from '@components/modals/SearchModal'
import { useWalletTokens } from 'hooks/useWalletTokens'
import FormatNumericValue from '@components/shared/FormatNumericValue'
import WalletIcon from '@components/icons/WalletIcon'
import { TokenMetadataItem } from 'types/token'
import {
  NumericFormat,
  NumberFormatValues,
  SourceInfo,
} from 'react-number-format'
import SwapTokenSelect from '@components/swap/SwapTokenSelect'
import { withValueLimit } from '@components/swap/SellInput'
import { useMarketTokensWithPrices } from 'hooks/useMarketTokens'
import useDebounce from 'hooks/useDebounce'
import { useTranslation } from 'next-i18next'

const set = store.getState().set

const LimitBuyInput = ({
  error,
  setError,
}: {
  error: string | undefined
  setError: (e: string) => void
}) => {
  const { t } = useTranslation('trade')
  const { walletTokens } = useWalletTokens()
  const outputAmountFromStore = store((s) => s.trade.limitForm.outputAmount)
  const outputToken = store((s) => s.swap.outputToken)
  const baseMint = store((s) => s.market.baseToken.mint)
  const [showSelectList, setShowSelectList] = useState(false)
  const { baseMintPrice, quoteMintPrice } = useMarketTokensWithPrices()
  const [
    debouncedOutputAmount,
    setDebouncedOutputAmount,
    immediateOutputAmount,
  ] = useDebounce<string | null>(null, 300)

  const isBid = outputToken.mint === baseMint

  const [walletBalance, buyDecimals] = useMemo(() => {
    if (!outputToken.mint) return ['0', 6]
    const walletBalance = walletBalanceForToken(walletTokens, outputToken.mint)
    const max = floorToDecimal(
      walletBalance.balance,
      walletBalance.decimals,
    ).toFixed()
    return [max, walletBalance.decimals]
  }, [walletTokens, outputToken.mint])

  const handleOutputAmountChange = useCallback(
    (e: NumberFormatValues, info: SourceInfo) => {
      if (info.source !== 'event') return

      const value = e.value === '.' ? '0.' : e.value
      setDebouncedOutputAmount(value)
    },
    [setDebouncedOutputAmount],
  )

  useEffect(() => {
    // skip setting on initial rendering
    if (debouncedOutputAmount == null) return

    set((s) => {
      s.trade.limitForm.outputAmount = debouncedOutputAmount
    })

    store
      .getState()
      .actions.setCalculatedLimitFormValuesFromOutputAmount(
        debouncedOutputAmount,
      )

    if (error) {
      setError('')
    }
  }, [debouncedOutputAmount, error, setError])

  const handleSelect = useCallback(
    async (newOutputMint: string, newOutputToken?: TokenMetadataItem) => {
      void store
        .getState()
        .actions.setNewOutputToken(newOutputMint, newOutputToken)

      setShowSelectList(false)
    },
    [],
  )

  return (
    <>
      <div className={`grid grid-cols-2 rounded-t-xl`}>
        <div className="col-span-2 mb-2 flex items-center justify-between">
          <p className="text-th-fgd-1">{t('limit.buy')}</p>
          <div className="flex items-center text-th-fgd-4">
            <WalletIcon className="mr-1 size-3" />
            <span className="font-mono text-xs">
              <FormatNumericValue
                value={walletBalance}
                decimals={buyDecimals}
              />
            </span>
          </div>
        </div>
        <div className="col-span-2 mb-2 flex items-center justify-between">
          <SwapTokenSelect
            imageUrl={outputToken.image_url}
            symbol={outputToken.symbol}
            showList={setShowSelectList}
          />
        </div>
        <div className="col-span-2">
          <div className="flex items-center">
            <NumericFormat
              inputMode="decimal"
              lang="en"
              allowedDecimalSeparators={['.', ',']}
              thousandSeparator=","
              allowNegative={false}
              valueIsNumericString
              decimalScale={buyDecimals || 6}
              name="amount"
              id="amount"
              className="box-border h-10 w-full bg-transparent font-mono text-xl text-th-fgd-1 focus:outline-none md:hover:bg-transparent"
              placeholder="0.00"
              value={immediateOutputAmount ?? outputAmountFromStore}
              onValueChange={handleOutputAmountChange}
              isAllowed={withValueLimit}
            />
            <p className="font-mono text-xs text-th-fgd-4">
              {outputAmountFromStore &&
              outputAmountFromStore !== '0' &&
              outputAmountFromStore !== '.'
                ? isBid
                  ? formatCurrencyValue(
                      +(outputAmountFromStore?.replace(/,/g, '') || '0') *
                        baseMintPrice,
                    )
                  : formatCurrencyValue(
                      +outputAmountFromStore.replace(/,/g, '') * quoteMintPrice,
                    )
                : '$0.00'}
            </p>
          </div>
        </div>
        {error ? (
          <div className="col-span-2 mt-1 flex justify-center">
            <InlineNotification
              type="error"
              desc={error}
              hideBorder
              hidePadding
            />
          </div>
        ) : null}
      </div>
      {showSelectList ? (
        <SearchModal
          isOpen={showSelectList}
          onClose={() => setShowSelectList(false)}
          onResultClick={handleSelect}
          type="outputToken"
        />
      ) : null}
    </>
  )
}

export default LimitBuyInput
